import { TFunction } from "i18next";

import { DATE_TIME_FORMAT_FILE_NAME } from "../../constants";
import {
  EntityPropertyChanges,
  EntityPropertyTypes,
  TimeZoneEnum,
} from "../../enums";
import { FormatDateTimeNormalizedFn } from "../../hooks";
import {
  EntityProperty,
  IAuditLogNormalized,
  IEntityPropertyArrayObject,
  IEntityPropertyArrayPrimitive,
  IEntityPropertyObject,
  IEntityPropertyPrimitive,
} from "../../models";
import { formatDate } from "../date.helpers";
import { saveCsvFile } from "../file-download.helpers";
import { AUDIT_LOG_CSV_INDENT_INCREMENT } from "./audit-log.constants";
import { getAuditLogCauserDisplayName } from "./audit-log.helpers";

const formatPropertyValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

const CHANGE_TYPE_LABELS: Record<EntityPropertyChanges, string> = {
  [EntityPropertyChanges.Added]: "Added",
  [EntityPropertyChanges.Changed]: "Changed",
  [EntityPropertyChanges.Removed]: "Removed",
  [EntityPropertyChanges.ItemsChanged]: "Changed",
};

const formatPrimitiveChange = (
  property: IEntityPropertyPrimitive | IEntityPropertyArrayPrimitive,
): string => {
  const oldValue = formatPropertyValue(property.oldValue);
  const newValue = formatPropertyValue(property.value);
  const changeType = CHANGE_TYPE_LABELS[property.changeType];

  if (property.changeType === EntityPropertyChanges.Removed) {
    return `${oldValue} (${changeType})`;
  }
  if (property.items) {
    const { added, removed, orderBefore, orderAfter } = property.items;
    const parts = [
      added.length ? `+ ${added.join(", ")}` : "",
      removed.length ? `- ${removed.join(", ")}` : "",
      orderBefore && orderAfter
        ? `order: ${orderBefore.join(" > ")} → ${orderAfter.join(" > ")}`
        : "",
    ].filter(Boolean);
    return `${parts.join("; ")} (${changeType})`;
  }
  if (oldValue && newValue && oldValue !== newValue) {
    return `${oldValue} → ${newValue} (${changeType})`;
  }
  return newValue ? `${newValue} (${changeType})` : "";
};

const buildHierarchicalChanges = (
  properties: EntityProperty[],
  indentLevel: number = 0,
): string[] => {
  const changes: string[] = [];
  const indent = "  ".repeat(indentLevel);

  for (const property of properties) {
    const { key, type } = property;

    switch (type) {
      case EntityPropertyTypes.Primitive:
      case EntityPropertyTypes.ArrayPrimitive: {
        const line = formatPrimitiveChange(
          property as IEntityPropertyPrimitive | IEntityPropertyArrayPrimitive,
        );
        if (line) {
          changes.push(`${indent}${key}: ${line}`);
        }
        break;
      }

      case EntityPropertyTypes.Object: {
        const objectProperty = property as IEntityPropertyObject;
        if (objectProperty.properties?.length) {
          changes.push(`${indent}${key}:`);
          changes.push(
            ...buildHierarchicalChanges(
              objectProperty.properties,
              indentLevel + 1,
            ),
          );
        }
        break;
      }

      case EntityPropertyTypes.ArrayObject: {
        const arrayObjectProperty = property as IEntityPropertyArrayObject;
        if (arrayObjectProperty.properties?.length) {
          changes.push(`${indent}${key}:`);
          arrayObjectProperty.properties.forEach(
            (arrayItem: EntityProperty[], index: number) => {
              changes.push(
                `${indent}  [${arrayObjectProperty.itemLabels?.[index] ?? index}]:`,
              );
              changes.push(
                ...buildHierarchicalChanges(
                  arrayItem,
                  indentLevel + AUDIT_LOG_CSV_INDENT_INCREMENT,
                ),
              );
            },
          );
        }
        break;
      }
    }
  }

  return changes;
};

export const exportAuditLogsToCsv = (
  auditLogs: IAuditLogNormalized[],
  formatDateTimeNormalized: FormatDateTimeNormalizedFn,
  translationFn?: TFunction,
): string => {
  if (!auditLogs?.length) {
    return "";
  }

  const logEntries: string[] = [];

  for (const log of auditLogs) {
    const formattedDate = formatDateTimeNormalized(log.createdAt);
    const userName =
      getAuditLogCauserDisplayName(log, translationFn) ||
      log.metadata?.userName ||
      "";

    // Determine if it's "Created by" or "Updated by" based on eventName
    const actionPrefix = log.eventName.toLowerCase().includes("created")
      ? "Created by"
      : "Updated by";

    const headerLine = `${actionPrefix} ${userName} at ${formattedDate}`;

    const changes = buildHierarchicalChanges(log.entityProperties, 1); // Start with indentLevel 1 for changes
    const changesText = changes.join("\n");

    logEntries.push(`${headerLine}\n${changesText}`);
  }

  // Join all log entries with an extra newline for separation between entries
  return logEntries.join("\n\n");
};

export const downloadAuditLogsCsv = (
  auditLogs: IAuditLogNormalized[],
  formatDateTimeNormalized: FormatDateTimeNormalizedFn,
  filenamePrefix?: string,
  translationFn?: TFunction,
): void => {
  const csvContent = exportAuditLogsToCsv(
    auditLogs,
    formatDateTimeNormalized,
    translationFn,
  );

  if (!csvContent) {
    return;
  }

  // Generate filename with seconds
  const now = new Date();
  const timestamp = formatDate({
    dateISO: now.toISOString(),
    dateFormat: DATE_TIME_FORMAT_FILE_NAME,
    timezone: TimeZoneEnum.UTC, // Use UTC for consistent file naming
  });

  const prefix = filenamePrefix || "audit-logs";
  const finalFilename = `${prefix}-${timestamp.replace(/ /g, "_")}.csv`;

  saveCsvFile(csvContent, finalFilename);
};
